import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { useSearchParams } from 'react-router';
import { QueueEntryStates } from '../../../functions/src/types';
import { useDataContext } from '../context/DataContext';
import { useFeedbackContext } from '../context/FeedbackContext';
import { extractUrlFromText } from '../Utils';
import { EmptyQueueIllustration } from './Illustrations';

export const Share: React.FC = () => {

  const [queuedBookmark, setQueuedBookmark] = React.useState<string | null | 'invalid'>(null);
  const [isOnline, setIsOnline] = React.useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const { showErrorMessage } = useFeedbackContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { queueEntries, queueBookmarkForSaving, dequeueBookmark } = useDataContext();

  React.useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  React.useEffect(() => {
    const sharedText = searchParams.get('text');
    // check that sharedText is a valid URL
    if (sharedText) {
      const cleanedText = extractUrlFromText(sharedText);
      if (cleanedText) {
        queueBookmarkForSaving(cleanedText);
        setQueuedBookmark(cleanedText);
      }
      else {
        showErrorMessage('Invalid URL');
        setQueuedBookmark('invalid');
      }
      setSearchParams(new URLSearchParams());
    }
  }, [searchParams, queueBookmarkForSaving, showErrorMessage, setSearchParams]);

  // Clear queued bookmark state once nothing is actively processing or failed.
  // ALREADY_SAVED entries are left visible for the user to dismiss, but don't
  // block the "Queue is clear" state from eventually appearing.
  React.useEffect(() => {
    if (queuedBookmark && queuedBookmark !== 'invalid') {
      const hasActiveOrFailed = queueEntries.some(e =>
        e.status === QueueEntryStates.PENDING ||
        e.status === QueueEntryStates.SAVING ||
        e.status === QueueEntryStates.FAILED
      );
      if (!hasActiveOrFailed) {
        setQueuedBookmark(null);
      }
    }
  }, [queueEntries, queuedBookmark]);

  const hasFailedEntries = queueEntries.some(entry => entry.status === QueueEntryStates.FAILED);
  const showRetryButton = queueEntries.length > 0 && hasFailedEntries && isOnline;

  const retryFailedEntries = () => {
    queueEntries
      .filter(entry => entry.status === QueueEntryStates.FAILED)
      .forEach(entry => queueBookmarkForSaving(entry.url));
  };

  const queueElements = [...(queueEntries ?? [])].reverse().map((entry, index) => {
    return (
      <Stack key={index} direction='column' spacing={0.5} id={`queueEntry${index}`} sx={{ width: '100%' }}>
        <Typography
          variant='caption'
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {entry.url}
        </Typography>
        <Stack direction='row' alignItems='center' spacing={1}>
          {entry.status === QueueEntryStates.FAILED && (
            <>
              <Chip
                icon={<ErrorOutlineIcon />}
                label="failed to save"
                variant="outlined"
                color="error"
                size="small"
              />
              <IconButton
                onClick={() => dequeueBookmark(entry.url)}
                size='small'
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
          {entry.status === QueueEntryStates.PENDING && (
            <Chip
              icon={<AccessTimeIcon />}
              label="pending"
              variant="outlined"
              size="small"
            />
          )}
          {entry.status === QueueEntryStates.SAVING && (
            <Chip
              icon={<SaveAltIcon />}
              label="saving"
              variant="outlined"
              color="primary"
              size="small"
            />
          )}
          {entry.status === QueueEntryStates.SAVED && (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label="saved"
              variant="outlined"
              color="success"
              size="small"
            />
          )}
          {entry.status === QueueEntryStates.ALREADY_SAVED && (
            <>
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label="already saved"
                variant="outlined"
                size="small"
              />
              <IconButton
                onClick={() => dequeueBookmark(entry.url)}
                size='small'
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Stack>
        {entry.status === QueueEntryStates.FAILED && entry.failureMessage && (
          <Typography
            variant='caption'
            color='error.main'
            sx={{ wordBreak: 'break-word' }}
          >
            {entry.failureMessage}
          </Typography>
        )}
        {entry.status === QueueEntryStates.ALREADY_SAVED && entry.existingTitle && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            Saved as: {entry.existingTitle}
          </Typography>
        )}
      </Stack>
    );
  });

  return (
    <Box justifyContent="center" alignItems='center' sx={{ p: 2 }}>
      <Stack justifyContent="center" alignItems="center" spacing={2}>
        {queuedBookmark === 'invalid' ? (
          <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />
        ) : queueElements.length === 0 && !queuedBookmark ? (
          <EmptyQueueIllustration />
        ) : showRetryButton ? (
          <Stack spacing={0.5} alignItems="center">
            <IconButton
              color="secondary"
              aria-label="retry failed"
              onClick={retryFailedEntries}
              sx={{ border: 1, borderColor: 'secondary.main' }}
            >
              <ReplayIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Retry saving
            </Typography>
          </Stack>
        ) : (
          <SaveAltIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        )}
        {queueElements.length === 0 && !queuedBookmark ? (
          <Stack spacing={1} alignItems="center">
            <Typography variant="h6" color="text.secondary">Queue is clear</Typography>
            <Link href='/'>
              <Typography variant='body1'>
                Go to bookmarks
              </Typography>
            </Link>
          </Stack>
        ) : (
          <Stack id='queue' spacing={0.5} sx={{ width: '100%' }}>
            {queueElements}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
